import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchMovieOptionsAdmin,
  addDistributor, updateDistributor, deleteDistributor,
  addAgeRating, updateAgeRating, deleteAgeRating,
} from '../api/client';
import { ageText } from './ageLabel';
import useWindowWidth from '../hooks/useWindowWidth';

/**
 * The admin's two fixed lists (§12.11): distributors and age ratings. The
 * movie menus only choose from these, so nothing about a film is typed twice.
 * Deleting an entry clears it from its films — a distributor goes back to
 * none, an age rating to TMDB's — and the confirmation says how many.
 */

const field = {
  background: 'var(--surf-alt)', border: '1px solid var(--brd)', borderRadius: 8, padding: '7px 10px',
  color: 'var(--txt)', fontSize: 13, fontFamily: 'Heebo, sans-serif', outline: 'none', minWidth: 0,
};
const btn = (kind) => ({
  padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
  fontFamily: 'Heebo, sans-serif',
  border: kind === 'primary' ? 'none' : '1px solid var(--brd)',
  background: kind === 'primary' ? 'var(--accent)' : 'transparent',
  color: kind === 'primary' ? '#fff' : kind === 'danger' ? 'var(--danger)' : 'var(--muted)',
});
const errText = (e) => e?.response?.data?.detail || 'הפעולה נכשלה';

function Row({ item, withAge, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [age, setAge] = useState(String(item.min_age ?? ''));
  useEffect(() => { setName(item.name); setAge(String(item.min_age ?? '')); }, [item]);
  if (editing) {
    return (
      <div data-list-row style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--brd)' }}>
        <input data-edit-name style={{ ...field, flex: 1 }} value={name} onChange={(e) => setName(e.target.value)} />
        {withAge && <input data-edit-age type="number" min={0} max={21} style={{ ...field, width: 64 }} value={age} onChange={(e) => setAge(e.target.value)} />}
        <button data-row-save style={btn('primary')} onClick={async () => { if (await onSave(name, Number(age))) setEditing(false); }}>שמור</button>
        <button style={btn()} onClick={() => setEditing(false)}>ביטול</button>
      </div>
    );
  }
  return (
    <div data-list-row style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--brd)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div data-row-name style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--txt)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {withAge ? ageText(item.name) : item.name}
        </div>
        <div style={{ fontSize: 11, color: 'var(--faint)' }}>
          {withAge && <>מגיל {item.min_age} · </>}{item.films} סרטים
        </div>
      </div>
      <button data-row-edit style={btn()} onClick={() => setEditing(true)}>עריכה</button>
      <button data-row-delete style={btn('danger')} onClick={onDelete}>מחיקה</button>
    </div>
  );
}

function ListCard({ title, items, withAge, onAdd, onSave, onDelete, error }) {
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const add = async () => {
    if (!name.trim() || (withAge && age === '')) return;
    if (await onAdd(name, Number(age))) { setName(''); setAge(''); }
  };
  return (
    <div data-list={withAge ? 'ages' : 'distributors'} style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--txt)', marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        <input data-add-name style={{ ...field, flex: 1 }} value={name} placeholder={withAge ? 'שם, למשל 16+' : 'שם המפיץ'}
               onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        {withAge && <input data-add-age type="number" min={0} max={21} style={{ ...field, width: 64 }} value={age} placeholder="גיל"
                           onChange={(e) => setAge(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />}
        <button data-add style={btn('primary')} onClick={add}>הוסף</button>
      </div>
      {error && <div data-list-error style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 4 }}>{error}</div>}
      <div>
        {items.map((it) => (
          <Row key={it.id} item={it} withAge={withAge} onSave={(n, a) => onSave(it, n, a)} onDelete={() => onDelete(it)} />
        ))}
      </div>
    </div>
  );
}

export default function MovieListsPanel() {
  const mob = useWindowWidth() < 768;
  const [lists, setLists] = useState(null);
  const [errors, setErrors] = useState({});
  const load = useCallback(() => fetchMovieOptionsAdmin().then(setLists).catch(() => setLists({ ages: [], distributors: [] })), []);
  useEffect(() => { load(); }, [load]);

  // Every action reloads the lists; a refusal (a duplicate name or age) shows under its list.
  const run = (which) => async (fn) => {
    setErrors((e) => ({ ...e, [which]: null }));
    try { await fn(); await load(); return true; } catch (e) { setErrors((x) => ({ ...x, [which]: errText(e) })); return false; }
  };
  const dist = run('distributors');
  const ages = run('ages');

  return (
    <div data-movie-lists style={{ background: 'var(--surf)', borderRadius: 16, padding: mob ? '16px' : '20px 24px', boxShadow: 'var(--shadow)' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--txt)', marginBottom: 14 }}>מפיצים והגבלות גיל</div>
      {!lists ? <div style={{ fontSize: 13, color: 'var(--faint)' }}>טוען...</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: mob ? 20 : 32 }}>
          <ListCard title="מפיצים" items={lists.distributors} error={errors.distributors}
            onAdd={(n) => dist(() => addDistributor(n))}
            onSave={(it, n) => dist(() => updateDistributor(it.id, n))}
            onDelete={(it) => window.confirm(`למחוק את "${it.name}"?${it.films ? ` ${it.films} סרטים יישארו בלי מפיץ.` : ''}`)
              && dist(() => deleteDistributor(it.id))} />
          <ListCard title="הגבלות גיל" withAge items={lists.ages} error={errors.ages}
            onAdd={(n, a) => ages(() => addAgeRating(n, a))}
            onSave={(it, n, a) => ages(() => updateAgeRating(it.id, n, a))}
            onDelete={(it) => window.confirm(`למחוק את "${it.name}"?${it.films ? ` ${it.films} סרטים יחזרו לדירוג של TMDB.` : ''}`)
              && ages(() => deleteAgeRating(it.id))} />
        </div>
      )}
    </div>
  );
}
